import { auth } from './firebase';

export const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

export const VIEW_AS_CLIENT_ID_KEY = 'heymotek_view_as_client_id';
export const VIEW_AS_CLIENT_NAME_KEY = 'heymotek_view_as_client_name';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: FormData | Record<string, unknown>;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  status: number;
  detail?: string;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.status = status;
    this.detail = detail;
    this.name = 'ApiError';
  }
}

async function getAuthHeader(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const token = await user.getIdToken();
    return `Bearer ${token}`;
  } catch {
    return null;
  }
}

export async function apiRequest<T = void>(
  endpoint: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;

  const requestHeaders: Record<string, string> = { ...headers };

  const authHeader = await getAuthHeader();
  if (authHeader) requestHeaders['Authorization'] = authHeader;

  const viewAs = localStorage.getItem(VIEW_AS_CLIENT_ID_KEY);
  if (viewAs) requestHeaders['X-View-As-Client'] = viewAs;

  let requestBody: FormData | string | undefined;
  if (body) {
    if (body instanceof FormData) {
      requestBody = body;
    } else {
      requestHeaders['Content-Type'] = 'application/json';
      requestBody = JSON.stringify(body);
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: requestHeaders,
    body: requestBody,
  });

  if (!response.ok) {
    let errorDetail: string | undefined;
    try {
      const errorBody = await response.json();
      errorDetail = errorBody.detail || errorBody.message;
    } catch {
      // Ignore JSON parse errors
    }
    throw new ApiError(
      response.status,
      `API request failed: ${response.statusText}`,
      errorDetail,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    throw new ApiError(
      response.status,
      'Unexpected empty response body',
      'Server returned success status but no data',
    );
  }

  return JSON.parse(text) as T;
}

export { ApiError };
